// Where the camera should look when something happens. Pure: a log entry
// and the civ in, a shot out ({ q, r, zoom, hold }) or null for "stay".
// Matt, playtest 4: "the map should animate itself as things happen...
// zoom the camera around, because nobody is sitting here with the mouse."

import { siteCentre } from './layout';
import { directionOf } from '@/engine/cards';

const at = ([q, r], zoom = 0.62, hold = 3800, extra = {}) => ({
  q, r, zoom, hold, ...extra,
});
// A shot with a camera move in it: slower in, turning and leaning as it
// goes, and holding long enough to watch the thing happen. Matt, 2026-09-20,
// on a new building: "it would be cool if that animation was a little bit
// slower, like it sort of zoomed in with a little bit more drama. It could
// even do a little bit of tilting and rotating around."
const DRAMA = { ms: 2400, swing: 0.55, tilt: -0.14 };
const TOWN = [0, 0];
const WIDE = { q: 0, r: 0, zoom: 1.15, hold: 3000 };

export const shotFor = (entry, civ = {}) => {
  if (!entry) return null;
  // A people met, or a people's card turned: their home, slowly (after
  // playtest 9 - the Neighbours were found and nobody could see who).
  const people = (entry.met || entry.rival) && (civ.rivals || []).find((r) => r.id === (entry.met || entry.rival));
  if (people) return at([people.q, people.r], 0.6, 6000, DRAMA);
  if (entry.kind === 'act') {
    // THE FRONTIER: wherever it happened, which the log now says (`stage`,
    // playtest 12). This used to look for the old single band, which is
    // gone, so every move and fight flew to a farm or the forest instead -
    // "a sort of random zoom in... I'm not that impressed".
    const st = entry.stage;
    // Close, and looking well down on it, so no tree or peak stands in front.
    if (st?.battle) return at(st.battle.at, 0.2, 0, { battle: st.battle, swing: 0.3, tilt: 0.28 });
    if (st?.at) {
      if (entry.action === 'move') return at(st.at, 0.55, 2600, { ms: 900 });
      return at(st.at, 0.5, 3200);
    }
    switch (entry.action) {
      // The Stewards: only what changes the town is worth a camera move.
      case 'build': case 'annex': {
        const city = civ.city || {};
        const hex = entry.action === 'build' ? city.buildings?.[city.buildings.length - 1]?.hex : city.hexes?.[city.hexes.length - 1];
        if (!hex) return null;
        // Raising something is the moment worth the camera move.
        return entry.action === 'build' ? at(hex, 0.42, 6000, DRAMA) : at(hex, 0.6);
      }
      case 'explore': return entry.direction !== undefined ? at(siteCentre(entry.direction, civ), 0.7, 5000) : null;
      case 'build-monument': return at(TOWN, 0.44, 6500, { ...DRAMA, swing: 0.8, ms: 2800 });
      // Everything else - a shift worked, a card played, an experiment -
      // changes nothing on the map, and a zoom for it is the "why did that
      // just zoom in" of playtest 12. The camera stays.
      default: return null;
    }
  }
  if (entry.kind === 'event') {
    // Something appeared on the map: look at it.
    const newest = (civ.menaces || [])[(civ.menaces || []).length - 1];
    if (['raiders', 'raiders2', 'bandits', 'wolves_bolder', 'cold_shoulder'].includes(entry.card)) return newest ? at([newest.q, newest.r], 0.7, 4500) : WIDE;
    if (entry.card === 'monument' || entry.card === 'anniversary') return at(TOWN, 0.55, 4500);
    if (entry.card === 'festival_remembered') return at(TOWN, 0.7);
    if (entry.card === 'valley_harvest' || entry.card === 'ore_find' || entry.card === 'merchants' || entry.card === 'envoys' || entry.card === 'omen_of_ruins' || entry.card === 'marsh_fever') {
      const region = { valley_harvest: 'fertile_valley', ore_find: 'copper_hills', merchants: 'old_road', envoys: 'neighbours', omen_of_ruins: 'ruins', marsh_fever: 'marsh' }[entry.card];
      const d = directionOf(civ, region);
      return d >= 0 ? at(siteCentre(d, civ), 0.7) : WIDE;
    }
    return WIDE;
  }
  if (entry.kind === 'upkeep' || entry.kind === 'order') return WIDE;
  return null;
};
